import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { IEquisModalDialogResponse as IModalResponse } from '@tn-equis/core/components/modal-dialog';
import { Subject, catchError, takeUntil, throwError } from 'rxjs';

import { ButtonDownloadAppService } from './button-download-app.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class WhatsappService implements OnDestroy {
  // FIELDS
  private tryCounter = 0;
  private phone!: string;
  private readonly MAX_TRIES = 3;
  private readonly BFF_BOT_URL = environment.whatsappServiceBffUrl;

  // PROPERTIES
  public eventHandler = new Subject<
    'error' | 'success' | 'retry' | 'retryCancel'
  >();
  private eventHandlerMap = new Map([
    ['error', (): void => this.onSendAppLinkError()],
    ['retry', (): void => this.onSendAppLinkRetry()],
    ['success', (): void => this.onSendAppLinkSuccess()],
  ]);
  private destroyed$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private dialog: ButtonDownloadAppService,
  ) {
    this.eventHandler
      .pipe(takeUntil(this.destroyed$))
      .subscribe(status => this.eventHandlerMap.get(status)?.());
  }

  // METHODS
  /**
   * Sends the download link to the user's Whatsapp
   * @param phone Whatsapp phone number to send the app download link
   */
  public sendAppLink(phone: string): void {
    // Store the number to use it later for further retries
    this.phone = phone;

    // Check if the POST must be done
    if (this.tryCounter++ < this.MAX_TRIES) {
      this.http
        .post(this.BFF_BOT_URL, { phoneNumber: phone })
        .pipe(catchError(error => throwError(() => new Error(error))))
        .subscribe({
          next: () => this.eventHandler.next('success'),
          error: () => this.eventHandler.next('retry'),
        });
    } else {
      // Throw error
      this.eventHandler.next('error');
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // PRIVATE METHODS
  private onSendAppLinkError(): void {
    this.resetFields();
    this.dialog.showStateDialog('error');
  }

  private onSendAppLinkRetry(): void {
    this.dialog
      .showStateDialog('retry')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ event, data }: IModalResponse) => {
        if (event === 'close_button' && data.text === 'Intentar de nuevo') {
          this.sendAppLink(this.phone);
        } else {
          this.eventHandler.next('retryCancel');
        }
      });
  }

  private onSendAppLinkSuccess(): void {
    this.dialog.showStateDialog('success');
    this.resetFields();
  }

  private resetFields(): void {
    this.phone = '';
    this.tryCounter = 0;
  }
}
